import { AddressElement } from '@stripe/react-stripe-js';

import { useState } from 'react';

import {
  TaxIdInfo,
  countryTaxIdInfo,
} from '@/Pages/Settings/utils/countryTaxIdInfo';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Select/Select';
import { cn } from '@/utils/tailwind';

import { useBusinessInfoSubForm } from '../utils/useBusinessInfoSubForm';

type Props = ReturnType<typeof useBusinessInfoSubForm>;

export const StripeStyleInput = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  warning,
  helperText,
  labelClassName,
  useStripeFont = true,
  disabled = false,
}: {
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: React.ReactNode;
  placeholder?: string;
  error?: string;
  warning?: string;
  helperText?: string;
  labelClassName?: string;
  useStripeFont?: boolean;
  disabled?: boolean;
}) => {
  return (
    <div className="flex flex-col gap-0 pl-0">
      <label
        className={cn(
          'mb-[2px] text-[14px] text-black dark:text-white',
          labelClassName
        )}
        htmlFor="taxId"
        style={{
          fontFamily: useStripeFont
            ? `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif`
            : 'inherit',
        }}
      >
        {label}
      </label>
      <input
        disabled={disabled}
        className={cn(
          'box-border  rounded-[5px] border-[1px] p-[.75rem] leading-3 transition-colors',
          'border-[rgb(230, 230, 230)] bg-transparent focus:border-[1px] focus:!outline-[3px] focus:outline-offset-0 focus:!outline-[#0b81fa]/40',
          'dark:border-[#424354] dark:bg-[#30313d] dark:text-white dark:focus:!border-[#3CC05D]/60 dark:focus:!outline-[#3CC05D]/30',
          'disabled:bg-secondary disabled:dark:bg-secondary'
        )}
        type="string"
        name="taxId"
        placeholder={placeholder}
        id="taxId"
        value={value}
        onChange={onChange}
      />
      {error ? (
        <div className="mt-[2px] text-error">{error}</div>
      ) : warning ? (
        <div className="mt-[2px] text-warning">{warning}</div>
      ) : (
        helperText && (
          <div className="mt-[2px] text-[14px] text-tertiary">{helperText}</div>
        )
      )}
    </div>
  );
};

const BusinessInfoSubForm = (props: Props) => {
  const {
    billingInfo,
    setAddressComplete,
    taxIdInfo,
    setTaxIdInfo,
    taxIdError,
    setTaxIdError,
    businessName,
    setBusinessName,
    isBusiness,
    setBillingInfo,
    taxId,
    setTaxId,
    email,
    setEmail,
    showBillingEmail,
    emailError,
  } = props;

  const [country, setCountry] = useState<string | undefined>(
    billingInfo?.address?.country
  );

  const [availableTaxIds, setAvailableTaxIds] = useState<TaxIdInfo[]>([]);

  return (
    <>
      {isBusiness && (
        <StripeStyleInput
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          label="Business Name"
          placeholder="Acme Corp"
        />
      )}
      {isBusiness && showBillingEmail && (
        <StripeStyleInput
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          label="Invoice email"
          placeholder=""
          error={emailError}
        />
      )}
      <AddressElement
        options={{
          mode: 'billing',
          defaultValues: {
            address: billingInfo?.address,
            name: billingInfo?.name,
          },
        }}
        onChange={(event) => {
          setBillingInfo(event.value);
          if (event.complete) {
            setAddressComplete(event.complete);
          }
          if (event.value.address.country !== country) {
            const taxIdInfo = countryTaxIdInfo(event.value.address.country);
            if (!taxIdInfo) {
              setTaxId('');
            }
            if (Array.isArray(taxIdInfo)) {
              setTaxIdInfo(taxIdInfo[0]);
              setAvailableTaxIds(taxIdInfo);
            } else {
              setTaxIdInfo(taxIdInfo);
              setAvailableTaxIds([]);
            }
            setCountry(event.value.address.country);
          }
        }}
      />

      {isBusiness && (
        <StripeStyleInput
          value={taxId}
          onChange={(e) => {
            setTaxId(e.target.value);
            setTaxIdError('');
            if (taxIdInfo && taxId.length > 0) {
              const regex = new RegExp(taxIdInfo.regex, 'g');
              const result = regex.test(taxId);
              if (!result) {
                setTaxIdError(
                  `${taxIdInfo.taxIdName} is in an unrecognized format.
                     However, if you're certain this ID is correct, you can still save the information.`
                );
              }
            }
          }}
          disabled={!taxIdInfo}
          placeholder={taxIdInfo?.example || 'Not required'}
          label={
            availableTaxIds.length > 1 ? (
              <Select
                value={taxIdInfo?.countryEnum}
                onValueChange={(value) => {
                  const selectedTaxId = availableTaxIds.find(
                    (taxId) => taxId.countryEnum === value
                  );
                  if (selectedTaxId) {
                    setTaxIdInfo(selectedTaxId);
                    setTaxId(''); // Clear the current tax ID when changing type
                  }
                }}
              >
                <SelectTrigger className="rounded-md border border-gray-300 p-1 px-2 text-sm">
                  <SelectValue placeholder="Select Tax ID Type" />
                </SelectTrigger>
                <SelectContent>
                  {availableTaxIds.map((taxId) => (
                    <SelectItem
                      key={taxId.countryEnum}
                      value={taxId.countryEnum}
                    >
                      {taxId.taxIdName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              taxIdInfo?.taxIdName || 'Tax ID'
            )
          }
          warning={taxIdError}
        />
      )}
    </>
  );
};

export { BusinessInfoSubForm };
